import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  getProjectAISettings,
  updateProjectAISettings,
  ProjectAISettings,
  ProjectAISettingsUpdate,
} from '../services/projectsApi';

interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Confirm Changes
        </h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to save these changes? This will update the AI settings for this project.
        </p>
        <div className="flex space-x-4 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Confirm & Save
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProjectSettingsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const projectIdNum = projectId ? parseInt(projectId, 10) : null;

  const [settings, setSettings] = useState<ProjectAISettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingData, setPendingData] = useState<ProjectAISettingsUpdate | null>(null);

  // Form data state
  const [formData, setFormData] = useState<ProjectAISettingsUpdate>({
    response_mode: 'auto_reply',
    default_prompt_id: null,
    model_settings: {
      model: 'gpt-4',
      temperature: 0.7,
      max_output_tokens: 1000,
    },
  });

  // Load settings on component mount
  useEffect(() => {
    if (projectIdNum) {
      loadSettings();
    }
  }, [projectIdNum]);

  const loadSettings = async () => {
    if (!projectIdNum) return;

    try {
      setLoading(true);
      setError(null);
      const projectSettings = await getProjectAISettings(projectIdNum);
      setSettings(projectSettings);

      // Update form data with loaded settings
      setFormData({
        response_mode: projectSettings.response_mode || 'auto_reply',
        default_prompt_id: projectSettings.default_prompt_id,
        model_settings: projectSettings.model_settings || {
          model: 'gpt-4',
          temperature: 0.7,
          max_output_tokens: 1000,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Show confirmation modal instead of saving directly
    setPendingData(formData);
    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    if (!projectIdNum || !pendingData) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      setShowConfirmation(false);

      const updatedSettings = await updateProjectAISettings(projectIdNum, pendingData);
      setSettings(updatedSettings);
      setSuccessMessage('Settings saved successfully!');
      setPendingData(null);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSave = () => {
    setShowConfirmation(false);
    setPendingData(null);
  };

  const handleReset = () => {
    if (settings) {
      setFormData({
        response_mode: settings.response_mode || 'auto_reply',
        default_prompt_id: settings.default_prompt_id,
        model_settings: settings.model_settings || {
          model: 'gpt-4',
          temperature: 0.7,
          max_output_tokens: 1000,
        },
      });
    }
    setError(null);
    setSuccessMessage(null);
  };

  const updateModelSetting = (key: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      model_settings: {
        ...prev.model_settings,
        [key]: value,
      },
    }));
  };

  if (!projectIdNum) {
    return (
      <div className="container py-8">
        <div className="text-center text-red-600">Invalid project ID</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container py-8">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  const modelSettings = formData.model_settings || {};

  return (
    <div className="container py-8">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Project AI Settings</h1>
          <p className="text-gray-600 mt-2">Configure AI settings for this project</p>
        </div>
        {projectIdNum && (
          <Link
            to={`/projects/${projectIdNum}/ai-setup`}
            className="btn-primary inline-flex items-center px-4 py-2 self-start"
          >
            Fast Router Setup Wizard
          </Link>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex">
            <svg className="w-5 h-5 text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div className="ml-3">
              <p className="text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">AI Configuration</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Response Mode */}
          <div>
            <label htmlFor="response_mode" className="block text-sm font-medium text-gray-700 mb-2">
              Response Mode
            </label>
            <select
              id="response_mode"
              name="response_mode"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={formData.response_mode || 'auto_reply'}
              onChange={(e) => setFormData({ ...formData, response_mode: e.target.value })}
            >
              <option value="auto_reply">Auto Reply</option>
              <option value="draft_for_approval">Draft for Approval</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              {formData.response_mode === 'auto_reply'
                ? 'AI will automatically send replies'
                : 'AI will create drafts that require approval before sending'}
            </p>
          </div>

          {/* Default Prompt ID */}
          <div>
            <label htmlFor="default_prompt_id" className="block text-sm font-medium text-gray-700 mb-2">
              Default Prompt ID (Optional)
            </label>
            <input
              type="number"
              id="default_prompt_id"
              name="default_prompt_id"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter prompt ID"
              value={formData.default_prompt_id || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  default_prompt_id: e.target.value ? parseInt(e.target.value, 10) : null,
                })
              }
            />
            <p className="text-sm text-gray-500 mt-1">
              ID of the default prompt to use for this project
            </p>
          </div>

          {/* Model Settings Section */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Model Settings</h3>

            {/* Model Name */}
            <div className="mb-4">
              <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">
                AI Model
              </label>
              <input
                type="text"
                id="model"
                name="model"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., gpt-4, gpt-5-nano-2025-08-07"
                value={modelSettings.model || ''}
                onChange={(e) => updateModelSetting('model', e.target.value)}
              />
            </div>

            {/* Temperature */}
            <div className="mb-4">
              <label htmlFor="temperature" className="block text-sm font-medium text-gray-700 mb-2">
                Temperature: {modelSettings.temperature ?? 0.7}
              </label>
              <input
                type="range"
                id="temperature"
                name="temperature"
                min="0"
                max="2"
                step="0.1"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                value={modelSettings.temperature ?? 0.7}
                onChange={(e) => updateModelSetting('temperature', parseFloat(e.target.value))}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Conservative (0.0)</span>
                <span>Balanced (1.0)</span>
                <span>Creative (2.0)</span>
              </div>
            </div>

            {/* Max Output Tokens */}
            <div className="mb-4">
              <label htmlFor="max_output_tokens" className="block text-sm font-medium text-gray-700 mb-2">
                Max Output Tokens
              </label>
              <input
                type="number"
                id="max_output_tokens"
                name="max_output_tokens"
                min="1"
                max="8000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., 1000"
                value={modelSettings.max_output_tokens || ''}
                onChange={(e) =>
                  updateModelSetting('max_output_tokens', e.target.value ? parseInt(e.target.value, 10) : undefined)
                }
              />
            </div>

            {/* Reasoning Effort */}
            <div className="mb-4">
              <label htmlFor="reasoning_effort" className="block text-sm font-medium text-gray-700 mb-2">
                Reasoning Effort (Optional)
              </label>
              <select
                id="reasoning_effort"
                name="reasoning_effort"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={modelSettings.reasoning?.effort || ''}
                onChange={(e) =>
                  updateModelSetting('reasoning', {
                    effort: e.target.value || undefined,
                  })
                }
              >
                <option value="">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            {/* Tools */}
            <div className="mb-4">
              <label htmlFor="tools" className="block text-sm font-medium text-gray-700 mb-2">
                Tools (comma-separated)
              </label>
              <input
                type="text"
                id="tools"
                name="tools"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., rag_search, gmail_reply, calendar_search"
                value={Array.isArray(modelSettings.tools) ? modelSettings.tools.join(', ') : ''}
                onChange={(e) => {
                  const tools = e.target.value
                    .split(',')
                    .map((t) => t.trim())
                    .filter((t) => t.length > 0);
                  updateModelSetting('tools', tools.length > 0 ? tools : undefined);
                }}
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter tool names separated by commas
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex space-x-4 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary px-6 py-2"
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelSave}
      />
    </div>
  );
};
